import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, Plus, Download } from "lucide-react";
import InventorySchedule from "@/components/stock/InventorySchedule";

const SELECT = "rounded-full border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300";
const ORANGE_SM = "rounded-full bg-[#FF6A00] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#E65F00]";
const fr = (d) => new Date(d).toLocaleDateString("fr-FR");
const signed = (e) => (e > 0 ? `+${e}` : e);
const tone = (e, zero = "text-zinc-500") => (e < 0 ? "text-red-400" : e > 0 ? "text-emerald-400" : zero);
const whLabel = (warehouses, id) => { const w = warehouses.find((x) => x.id === id); return w ? w.name : (id ? "entrepôt" : "Global"); };

function downloadCsv(rows, warehouses, period) {
  const head = ["Article", "Entrepôt", "Période", "Théorique", "Compté", "Écart", "Compté par", "Date"];
  const esc = (v) => { const s = String(v ?? ""); return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = rows.map((c) => [c.item_name, whLabel(warehouses, c.location_id), c.week || "", c.theoretical, c.counted, c.ecart, c.counted_by || "", c.counted_at ? fr(c.counted_at) : ""].map(esc).join(";"));
  const url = URL.createObjectURL(new Blob(["\ufeff" + [head.join(";"), ...lines].join("\r\n")], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `ecarts-inventaire${period !== "all" ? `-${period}` : ""}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function PendingTasks({ tasks, members, onLaunch, onAssign, onCount }) {
  return (
    <div data-testid="inventory-tasks-panel">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-[#FFD500]" /><p className="text-sm font-semibold text-white">Inventaires à faire</p></div>
        <Button data-testid="launch-inventory-btn" onClick={onLaunch} className={ORANGE_SM}><Plus className="mr-1 h-4 w-4" />Lancer un inventaire maintenant</Button>
      </div>
      {tasks.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-900/40 py-12 text-center">
          <ClipboardCheck className="mx-auto h-10 w-10 text-zinc-700" />
          <p className="mt-3 font-medium text-white">Aucun inventaire en attente</p>
          <p className="mt-1 text-sm text-zinc-500">Les comptages automatiques apparaîtront ici selon la planification.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => (
            <div key={t.id} data-testid={`inv-task-${t.id}`} className="flex flex-col gap-2 rounded-xl border border-[#FFD500]/30 bg-[#FFD500]/5 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-white">{t.week} · {(t.items || []).length} article(s) à recompter{t.assigned_name ? ` · assigné à ${t.assigned_name}` : ""}</p>
                <p className="text-xs text-zinc-500">{(t.items || []).map((i) => i.name).join(", ")}</p>
              </div>
              <div className="flex items-center gap-2">
                <select data-testid={`inv-task-assign-${t.id}`} value={t.assigned_to || ""} onChange={(e) => onAssign(t.id, e.target.value)} className="rounded-full border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-xs text-zinc-300">
                  <option value="">Assigner à…</option>
                  {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <Button data-testid={`inv-task-done-${t.id}`} onClick={() => onCount(t)} className={ORANGE_SM}>Faire le comptage</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DoneTasks({ tasks }) {
  if (!tasks.length) return null;
  return (
    <div data-testid="inventory-history-panel">
      <p className="mb-3 text-sm font-semibold text-white">Historique des inventaires réalisés</p>
      <div className="space-y-2">
        {tasks.map((t) => (
          <div key={t.id} data-testid={`inv-done-${t.id}`} className="flex flex-col gap-1 rounded-xl border border-zinc-800 bg-zinc-900 p-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-white">{t.week} · {(t.items || []).length} article(s)</p>
              <p className="text-xs text-zinc-500">{(t.items || []).map((i) => i.name).join(", ")}</p>
            </div>
            <span className="text-xs text-emerald-400">Fait{t.done_by ? ` par ${t.done_by}` : ""}{t.done_at ? ` · ${fr(t.done_at)}` : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportTable({ rows, warehouses }) {
  const ecartCount = rows.filter((c) => (c.ecart || 0) !== 0).length;
  const net = rows.reduce((s, c) => s + (c.ecart || 0), 0);
  return (
    <>
      <div className="mb-2 flex flex-wrap gap-4 text-xs text-zinc-400">
        <span><strong className="text-white">{ecartCount}</strong> article(s) avec écart</span>
        <span>Écart net : <strong className={tone(net, "text-white")}>{signed(net)}</strong></span>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-zinc-800">
        <table className="w-full text-left text-sm">
          <thead className="bg-zinc-900/60 text-xs uppercase text-zinc-500">
            <tr><th className="px-4 py-2.5">Article</th><th className="px-4 py-2.5">Entrepôt</th><th className="px-4 py-2.5">Période</th><th className="px-4 py-2.5 text-right">Théorique</th><th className="px-4 py-2.5 text-right">Compté</th><th className="px-4 py-2.5 text-right">Écart</th><th className="px-4 py-2.5">Par</th></tr>
          </thead>
          <tbody>
            {rows.map((c) => { const e = c.ecart || 0; return (
              <tr key={c.id} data-testid={`report-row-${c.id}`} className="border-t border-zinc-800/60">
                <td className="px-4 py-2.5 text-white">{c.item_name}</td>
                <td className="px-4 py-2.5 text-zinc-400">{whLabel(warehouses, c.location_id)}</td>
                <td className="px-4 py-2.5 text-zinc-400">{c.week || "—"}</td>
                <td className="px-4 py-2.5 text-right text-zinc-400">{c.theoretical}</td>
                <td className="px-4 py-2.5 text-right text-zinc-200">{c.counted}</td>
                <td className={`px-4 py-2.5 text-right font-semibold ${tone(e)}`}>{signed(e)}</td>
                <td className="px-4 py-2.5 text-xs text-zinc-500">{c.counted_by || "—"}{c.counted_at ? ` · ${fr(c.counted_at)}` : ""}</td>
              </tr>
            ); })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function DiscrepancyReport({ counts, warehouses }) {
  const [period, setPeriod] = useState("all");
  const [wh, setWh] = useState("all");
  const [all, setAll] = useState(false);
  const weeks = [...new Set(counts.map((c) => c.week).filter(Boolean))].sort().reverse();
  const rows = counts
    .filter((c) => period === "all" || c.week === period)
    .filter((c) => wh === "all" || (c.location_id || "") === wh)
    .filter((c) => all || (c.ecart || 0) !== 0);
  return (
    <div data-testid="inventory-report-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-white">Rapport d'écarts d'inventaire</p>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="report-period" value={period} onChange={(e) => setPeriod(e.target.value)} className={SELECT}>
            <option value="all">Toutes les périodes</option>
            {weeks.map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
          {warehouses.length > 0 && (
            <select data-testid="report-warehouse" value={wh} onChange={(e) => setWh(e.target.value)} className={SELECT}>
              <option value="all">Tous les entrepôts</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          )}
          <label className="flex items-center gap-1.5 text-xs text-zinc-400"><input data-testid="report-show-all" type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} className="h-3.5 w-3.5 accent-[#FF6A00]" />Afficher tous les comptages</label>
          <Button data-testid="report-export-csv" onClick={() => downloadCsv(rows, warehouses, period)} disabled={rows.length === 0} variant="outline" className="rounded-full border-zinc-700 px-3 py-1.5 text-xs text-zinc-200 hover:bg-zinc-800 disabled:opacity-40"><Download className="mr-1 h-3.5 w-3.5" />Export CSV</Button>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-900/40 py-10 text-center">
          <p className="text-sm text-zinc-500">{all ? "Aucun comptage enregistré pour ce filtre." : "Aucun écart détecté pour ce filtre 🎉"}</p>
        </div>
      ) : <ReportTable rows={rows} warehouses={warehouses} />}
    </div>
  );
}

export default function InventoryPanel({ tasks, members, counts, warehouses, onLaunch, onAssign, onCount, isAdmin }) {
  return (
    <div className="space-y-6" data-testid="inventory-tab">
      <InventorySchedule isAdmin={isAdmin} />
      <PendingTasks tasks={tasks.filter((t) => t.status !== "fait")} members={members} onLaunch={onLaunch} onAssign={onAssign} onCount={onCount} />
      <DoneTasks tasks={tasks.filter((t) => t.status === "fait")} />
      <DiscrepancyReport counts={counts} warehouses={warehouses} />
    </div>
  );
}
